<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { injectionKeys as litiumKeys } from '@motillo/drapejs-litium';
import useContext from '@/composables/useContext';
import * as config from '@/config';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { configuratorStateKey } from '../ProductConfiguratorKeys';

const SIZE_LIMIT_IN_MB = 5;
const { isAuthorized, route, navigateToLoginPage, channel, user } =
  useContext();
const toLitiumMediaUrl = inject(litiumKeys.toLitiumMediaUrlKey, () => '');
const { websiteText } = useWebsiteTexts();
const emit = defineEmits(['upload', 'remove']);
const { configuratorState } = inject(configuratorStateKey);

const props = defineProps({
  upload: {
    type: Object,
    default: null
  }
});

const hasImageSizeWarning = ref(false);
const displayFileSize = computed(() => {
  if (!props.upload?.Size) return '';
  const { Size } = props.upload;
  return Size < 1 ? `${Math.round(Size * 1024)} kB` : `${Math.round(Size)} mB`;
});
const imageSource = computed(() => {
  const id = props.upload.SystemId;
  return toLitiumMediaUrl(id, { maxWidth: 124 });
});

const showDefaultImage = ref(false);

function displayDefaultImage() {
  showDefaultImage.value = true;
}

const requestContext = computed(() => ({
  channelSystemId: channel.value.systemId,
  websiteSystemId: channel.value.website?.systemId,
  culture: channel.value.locale
}));

function removeImage() {
  showDefaultImage.value = false;
  emit('remove');
}

function handleImage(e) {
  if (!isAuthorized()) {
    const search = route?.query || {};
    const query = {
      ...search,
      progress: 'product-configuration'
    };

    sessionStorage.setItem(
      'product-configuration',
      JSON.stringify(configuratorState.value)
    );
    navigateToLoginPage(query);
    return;
  }

  if (!e.target.files || e.target.files.length === 0) {
    return;
  }
  hasImageSizeWarning.value = false;
  const image = e.target.files[0];
  const imgSize = parseFloat((image.size / 1024 / 1024).toFixed(5));

  if (imgSize > SIZE_LIMIT_IN_MB) {
    hasImageSizeWarning.value = true;
    return;
  }

  const formData = new FormData();
  formData.append('file', image);
  formData.append('user', user.value?.organization?.organizationNo);

  fetch(`${config.litiumBaseUrl}/api/productprint`, {
    method: 'POST',
    body: formData,
    headers: {
      'litium-request-context': JSON.stringify(requestContext.value)
    }
  })
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }

      return response.json();
    })
    .then((data) => {
      const imageData = data;
      imageData.Size = imgSize;
      emit('upload', imageData);
    })
    .catch((error) => {
      console.log('Couldnt upload image', error);
    });
}
</script>
<template>
  <div class="decoration-upload">
    <p class="decoration-upload__title">
      {{ websiteText('decoration__add_logo').value }}
    </p>
    <label
      v-if="!upload"
      for="decoration-upload__file-upload"
      class="secondary-button decoration-upload__upload-label"
    >
      <input
        type="file"
        id="decoration-upload__file-upload"
        class="decoration-upload__upload-button"
        accept=".eps, .pdf, .ai, .png, .jpg, .jpeg, .ps"
        @change="handleImage"
      />
      <img src="../../../static/icons/upload_icon.svg" alt="upload-icon" />
      {{ websiteText('global__select_file').value }}
    </label>
    <div class="decoration-upload__error-message" v-if="hasImageSizeWarning">
      {{
        websiteText('decoration__size_error', {
          maxsize: SIZE_LIMIT_IN_MB.toString()
        }).value
      }}
    </div>
    <p v-if="!upload" class="decoration-upload__recommended-types-text">
      {{ websiteText('decoration__file_recommendation').value }}
    </p>
    <p v-if="!upload" class="decoration-upload__recommended-types-examples">
      {{ websiteText('decoration__file_examples').value }}
    </p>
    <div class="decoration-upload__uploaded" v-if="upload">
      <img
        v-if="!showDefaultImage"
        class="decoration-upload__uploaded-image"
        :src="imageSource"
        alt="Your uploaded image"
        @error="displayDefaultImage"
      />
      <img
        v-if="showDefaultImage"
        class="decoration-upload__uploaded-image"
        src="../../../static/images/fallback_image.png"
        alt="Your uploaded image"
      />
      <div class="decoration-upload__uploaded-description">
        <div class="decoration-upload__uploaded-texts">
          <p class="decoration-upload__uploaded-name">
            {{ upload.Name }}
          </p>
          <p class="decoration-upload__uploaded-size">
            {{ displayFileSize }}
          </p>
        </div>
        <img
          class="decoration-upload__uploaded-remove"
          src="@/static/icons/trash_icon.svg"
          alt="trashcan icon"
          @click="removeImage"
        />
      </div>
    </div>
  </div>
</template>

<style>
.decoration-upload {
  margin-bottom: 29px;
}

.decoration-upload__title {
  color: var(--color-text-darker);
  font-size: 14px;
  margin: 0 0 15px;
}

.secondary-button.decoration-upload__upload-label {
  display: inline-grid;
  grid-auto-flow: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  margin-bottom: 8px;
}

.decoration-upload__upload-button {
  display: none;
}

.decoration-upload__uploaded {
  display: flex;
  padding: 10px 12px;
  border-radius: 7px;
  border: 1px solid var(--color-neutrals-20);
}

.decoration-upload__uploaded-image {
  width: 62px;
  height: 62px;
  margin-right: 10px;
  object-fit: contain;
}

.decoration-upload__uploaded-description {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.decoration-upload__uploaded-name {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-darker);
}

.decoration-upload__uploaded-size {
  margin: 0;
  font-size: 12px;
  line-height: 17px;
  color: var(--color-text-medium);
}

.decoration-upload__uploaded-remove {
  display: flex;
  align-self: center;
  cursor: pointer;
}

.decoration-upload__recommended-types-text,
.decoration-upload__recommended-types-examples {
  font-size: 12px;
  line-height: 17px;
  color: var(--color-text-medium);
  margin: 0;
}

.decoration-upload__error-message {
  color: var(--color-feedback-error);
  font-size: 12px;
  line-height: 17px;
}
</style>
